<template>
  <v-col>
    <v-dialog
      max-width="500"
      height="375"
      v-model="didNotFindItemDialog"
      @click:outside="didNotFindItemDialog = false"
    >
      <v-card
        ><v-col>
          <p class="bold-h3">{{ $t("didNotFindItem") }}</p>
          <p class="body-2 mt-4">{{ $t("whyDidNotFind") }}</p>
          <v-select
            class="mt-2"
            v-model="whyDidNotFind"
            variant="outlined"
            bg-color="surface"
            item-title="value"
            :items="[
              { name: 'itemTooFarDown', value: 'item_far_down' },
              { name: 'itemMissing', value: 'item_not_exists' },
              { name: 'changedKeywords', value: 'change_term' },
              { name: 'other', value: 'other' },
            ]"
            density="compact"
            single-line
          >
            <template #selection="item">
              <v-row class="pt-2 pb-2 ma-0 body-2" align="center"
                ><p>{{ $t(item.item.raw.name) }}</p>
              </v-row>
            </template>
            <template #item="{ item, props: { onClick } }">
              <v-list-item @click="onClick" justify="center" class="body-3">
                <v-row class="pa-0 ma-0" align="center">
                  <p>{{ $t(item.raw.name) }}</p></v-row
                >
              </v-list-item>
            </template></v-select
          >

          <v-text-field
            v-if="whyDidNotFind != 'item_far_down'"
            :label="
              whyDidNotFind == 'other'
                ? $t('other')
                : whyDidNotFind == 'change_term'
                ? $t('whatKeywordsDidYouChange')
                : $t('whatItemWereYouLookingFor')
            "
            v-model="whyDidNotFindText"
            color="primary"
            bg-color="surface"
            variant="outlined"
            single-line
            class="body-1 mt-4"
          ></v-text-field>
        </v-col>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            @click="didNotFindItemDialog = false"
            color="error"
            variant="flat"
            class="button text-none"
          >
            {{ $t("cancel") }}</v-btn
          >
          <v-btn
            @click="sendReport()"
            color="primary"
            variant="flat"
            class="button text-none"
          >
            {{ $t("done") }}</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>
    <!-- Vuetify Loading Component -->

    <!-- Content to display when not loading -->

    <PhoneSearchField v-if="isPhone || isTablet" />
    <Breadcrumbs :items="breadcrumbItems" />

    <v-col class="pa-0">
      <v-row align="center" justify="space-between" class="pa-0 ma-0 mb-10">
        <p class="bold-h1 pb-2">{{ searchTerm }}</p>
        <v-btn
          variant="text"
          color="primary"
          class="button text-none"
          @click="didNotFindItemDialog = true"
          >{{ $t("didNotFindItem") }}</v-btn
        ></v-row
      >
      <v-row v-if="loading" align="center" justify="center">
        <v-progress-circular
          indeterminate
          color="primary"
        ></v-progress-circular>
      </v-row>

      <AllProducts
        v-else
        :title="title"
        :products="products"
        :loadingMoreProducts="loadingMoreProducts"
        @loadMore="handleLoadMore"
      />
    </v-col>
  </v-col>
</template>

<script>
import AllProducts from "../AllProducts.vue";
import CategoryProductAPI from "@/api/CategoryProductAPI";
import { useI18n } from "vue-i18n";
import { mapFields } from "vuex-map-fields";
import { mapState } from "vuex";
import Breadcrumbs from "../Breadcrumbs.vue";
import { search_product } from "../../plugins/analytics.js";
/**
 * Component for displaying search results.
 * It fetches and displays products based on the search term from the route parameter.
 * It also redirects to the homepage if the storeId is undefined and reloads the API requests if the storeId changes.
 */
export default {
  name: "SearchResults",
  components: {
    AllProducts,
    Breadcrumbs,
  },
  data() {
    return {
      products: [],
      page: 1,
      isPhone: false,
      isTablet: false,
      loadingMoreProducts: false,
      loading: true,
      didNotFindItemDialog: false,
      whyDidNotFind: "item_far_down",
      whyDidNotFindText: "",
    };
  },
  computed: {
    ...mapFields(["searchTerm"]),
    ...mapState(["currentLang"]),
    breadcrumbItems() {
      return [
        {
          text: this.t("home"),
          to: {
            name: "handla",
          },
        },
        { text: `${this.t("search")}: ${this.searchTerm}`, disabled: true },
      ];
    },
  },
  setup() {
    const { t, locale } = useI18n();
    return { t, locale };
  },
  watch: {
    async searchTerm(newVal, oldVal) {
      if (this.loading == false) {
        this.loading = true;
        this.products = [];
        this.page = 1;
        await this.fetchProducts();
        this.search_product(this.searchTerm);
        this.loading = false;
      }
    },
    $route: async function (newRoute, oldRoute) {
      if (
        newRoute.query.q != undefined &&
        newRoute.query.q != this.searchTerm
      ) {
        this.searchTerm = newRoute.query.q;
      }
    },
    currentLang(newVal) {
      this.translateProducts();
    },
  },
  methods: {
    search_product,
    async translateProducts() {
      try {
        const limit = this.products.length;
        const response = await CategoryProductAPI.getProducts({
          searchString: this.searchTerm,
          limit: limit,
        });
        this.products = response.data;
      } catch (error) {
        console.error("Error fetching products:", error);
      }
    },
    async fetchProducts() {
      try {
        const response = await CategoryProductAPI.getProducts({
          searchString: this.searchTerm,
          page: this.page,
        });
        this.products = [...this.products, ...response.data];
      } catch (error) {
        console.error("Error fetching products:", error);
      }
    },
    async handleLoadMore() {
      this.loadingMoreProducts = true;
      this.page += 1;
      await this.fetchProducts();
      this.loadingMoreProducts = false;
    },

    async sendReport() {
      console.log("SENDING REPORT");
      try {
        this.didNotFindItemDialog = false;

        await CategoryProductAPI.sendSearchFeedback(
          this.searchTerm,
          this.whyDidNotFindText,
          this.whyDidNotFind
        );

        console.log(this.whyDidNotFind);
      } catch (error) {
        console.error("Error sending search feedback:", error);
      }
    },
  },
  async activated() {
    if (this.$route.query.q != undefined) {
      this.searchTerm = this.$route.query.q;
    }
    this.products = [];
    this.page = 1;
    await this.fetchProducts();
    this.loading = false;
    this.search_product(this.searchTerm);
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.checkDevice);
  },
};
</script>

<style scoped>
.breadcrumbs {
  max-width: 1300px;
  margin: auto;
  padding-left: 20px;
}
.grid-container {
  width: 100%;
  margin-bottom: 125px;
}
</style>
